/**
 * Citations Footer Component
 * Displays medical references and sources at the bottom of content pages.
 * Improves AI visibility by providing authoritative source attribution.
 */

import { useLanguage } from "@/contexts/LanguageContext";
import { BookOpen, ExternalLink, Shield } from "lucide-react";

export interface Citation {
  id: number;
  title: string;
  source: string;
  url: string;
  year?: string;
}

// Pre-built citation libraries by medical category
export const DENTAL_CITATIONS: Citation[] = [
  { id: 1, title: "Dental Implant Success Rates: A Systematic Review", source: "Journal of Dental Research", url: "https://pubmed.ncbi.nlm.nih.gov/25715576/", year: "2015" },
  { id: 2, title: "Porcelain Veneers: A Review of the Literature", source: "Journal of Dentistry", url: "https://pubmed.ncbi.nlm.nih.gov/21288586/", year: "2011" },
  { id: 3, title: "Effectiveness of Professional Teeth Whitening", source: "Cochrane Database of Systematic Reviews", url: "https://pubmed.ncbi.nlm.nih.gov/29786833/", year: "2018" },
  { id: 4, title: "Orthodontic Treatment Outcomes with Clear Aligners", source: "American Journal of Orthodontics", url: "https://pubmed.ncbi.nlm.nih.gov/30268258/", year: "2019" },
  { id: 5, title: "Root Canal Treatment: Current Concepts and Techniques", source: "International Endodontic Journal", url: "https://pubmed.ncbi.nlm.nih.gov/30891811/", year: "2019" },
];

export const DERMATOLOGY_CITATIONS: Citation[] = [
  { id: 1, title: "Botulinum Toxin in Aesthetic Medicine: Safety and Efficacy", source: "Dermatologic Surgery", url: "https://pubmed.ncbi.nlm.nih.gov/28498158/", year: "2017" },
  { id: 2, title: "Hyaluronic Acid Fillers: Science and Clinical Uses", source: "Clinical, Cosmetic and Investigational Dermatology", url: "https://pubmed.ncbi.nlm.nih.gov/24348056/", year: "2013" },
  { id: 3, title: "Skin Rejuvenation Using PRP: A Systematic Review", source: "Journal of Cosmetic Dermatology", url: "https://pubmed.ncbi.nlm.nih.gov/30681262/", year: "2019" },
  { id: 4, title: "Exosome Therapy in Dermatology: Current Evidence", source: "Stem Cell Research & Therapy", url: "https://pubmed.ncbi.nlm.nih.gov/33397467/", year: "2021" },
  { id: 5, title: "Sculptra (Poly-L-Lactic Acid): Long-term Safety Data", source: "Aesthetic Surgery Journal", url: "https://pubmed.ncbi.nlm.nih.gov/25805282/", year: "2015" },
];

export const WEIGHT_LOSS_CITATIONS: Citation[] = [
  { id: 1, title: "Non-Surgical Body Contouring: Evidence-Based Review", source: "Aesthetic Surgery Journal", url: "https://pubmed.ncbi.nlm.nih.gov/28207042/", year: "2017" },
  { id: 2, title: "Cryolipolysis for Fat Reduction: Safety and Efficacy", source: "Lasers in Surgery and Medicine", url: "https://pubmed.ncbi.nlm.nih.gov/25443439/", year: "2014" },
  { id: 3, title: "Injectable Lipolysis: Mechanism and Clinical Outcomes", source: "Journal of Clinical and Aesthetic Dermatology", url: "https://pubmed.ncbi.nlm.nih.gov/26029337/", year: "2015" },
  { id: 4, title: "GLP-1 Receptor Agonists for Weight Management", source: "The New England Journal of Medicine", url: "https://pubmed.ncbi.nlm.nih.gov/33567185/", year: "2021" },
  { id: 5, title: "Radiofrequency Body Contouring: A Comprehensive Review", source: "Journal of Drugs in Dermatology", url: "https://pubmed.ncbi.nlm.nih.gov/28898455/", year: "2017" },
];

export const HAIR_CITATIONS: Citation[] = [
  { id: 1, title: "DHI vs FUE Hair Transplant: Comparative Study", source: "Journal of Cutaneous and Aesthetic Surgery", url: "https://pubmed.ncbi.nlm.nih.gov/31619877/", year: "2019" },
  { id: 2, title: "PRP for Androgenetic Alopecia: A Systematic Review", source: "Dermatologic Surgery", url: "https://pubmed.ncbi.nlm.nih.gov/30882488/", year: "2019" },
  { id: 3, title: "Hair Transplant Graft Survival Rates", source: "International Journal of Trichology", url: "https://pubmed.ncbi.nlm.nih.gov/28932059/", year: "2017" },
  { id: 4, title: "Mesotherapy for Hair Loss: Clinical Evidence", source: "Journal of Cosmetic Dermatology", url: "https://pubmed.ncbi.nlm.nih.gov/29178400/", year: "2018" },
  { id: 5, title: "Stem Cell Therapy in Hair Restoration", source: "Stem Cells Translational Medicine", url: "https://pubmed.ncbi.nlm.nih.gov/31621208/", year: "2020" },
];

export const LASER_CITATIONS: Citation[] = [
  { id: 1, title: "Laser Hair Removal: Updated Guidelines", source: "Journal of the American Academy of Dermatology", url: "https://pubmed.ncbi.nlm.nih.gov/30654070/", year: "2019" },
  { id: 2, title: "Diode Laser vs Alexandrite for Hair Removal", source: "Lasers in Medical Science", url: "https://pubmed.ncbi.nlm.nih.gov/29282573/", year: "2018" },
  { id: 3, title: "Safety of Laser Treatments on Dark Skin Types", source: "Dermatologic Surgery", url: "https://pubmed.ncbi.nlm.nih.gov/27340740/", year: "2016" },
  { id: 4, title: "Fractional CO2 Laser for Skin Rejuvenation", source: "Journal of Cosmetic and Laser Therapy", url: "https://pubmed.ncbi.nlm.nih.gov/28277023/", year: "2017" },
  { id: 5, title: "Long-term Outcomes of Laser Hair Reduction", source: "Lasers in Surgery and Medicine", url: "https://pubmed.ncbi.nlm.nih.gov/30653264/", year: "2019" },
];

export const SURGERY_CITATIONS: Citation[] = [
  { id: 1, title: "Rhinoplasty: Current Techniques and Outcomes", source: "Aesthetic Plastic Surgery", url: "https://pubmed.ncbi.nlm.nih.gov/30539272/", year: "2019" },
  { id: 2, title: "Liposuction Safety: A Large-Scale Review", source: "Plastic and Reconstructive Surgery", url: "https://pubmed.ncbi.nlm.nih.gov/29369992/", year: "2018" },
  { id: 3, title: "Abdominoplasty Outcomes and Patient Satisfaction", source: "Annals of Plastic Surgery", url: "https://pubmed.ncbi.nlm.nih.gov/29461292/", year: "2018" },
  { id: 4, title: "Bariatric Surgery: Long-term Weight Loss Outcomes", source: "The Lancet", url: "https://pubmed.ncbi.nlm.nih.gov/26802856/", year: "2016" },
  { id: 5, title: "Patient Safety in Cosmetic Surgery: Guidelines", source: "British Journal of Surgery", url: "https://pubmed.ncbi.nlm.nih.gov/30311627/", year: "2018" },
];

export const GENERAL_CITATIONS: Citation[] = [
  { id: 1, title: "Dubai Health Authority: Healthcare Standards", source: "DHA Official", url: "https://www.dha.gov.ae", year: "2024" },
  { id: 2, title: "Patient Safety in Ambulatory Healthcare", source: "World Health Organization", url: "https://www.who.int/teams/integrated-health-services/patient-safety", year: "2023" },
  { id: 3, title: "Evidence-Based Medicine in Clinical Practice", source: "BMJ Evidence-Based Medicine", url: "https://pubmed.ncbi.nlm.nih.gov/30442714/", year: "2019" },
  { id: 4, title: "Quality Standards for Healthcare Facilities", source: "Joint Commission International", url: "https://www.jointcommissioninternational.org/", year: "2024" },
  { id: 5, title: "Patient-Centered Care: A Systematic Review", source: "Patient Education and Counseling", url: "https://pubmed.ncbi.nlm.nih.gov/30527653/", year: "2019" },
];

// Map department to citations
export function getCitationsForDepartment(department: string): Citation[] {
  const deptLower = department.toLowerCase();
  if (deptLower.includes("dental") || deptLower.includes("أسنان")) return DENTAL_CITATIONS;
  if (deptLower.includes("derma") || deptLower.includes("جلد")) return DERMATOLOGY_CITATIONS;
  if (deptLower.includes("weight") || deptLower.includes("وزن")) return WEIGHT_LOSS_CITATIONS;
  if (deptLower.includes("hair") || deptLower.includes("شعر")) return HAIR_CITATIONS;
  if (deptLower.includes("laser") || deptLower.includes("ليزر")) return LASER_CITATIONS;
  if (deptLower.includes("surg") || deptLower.includes("جراح")) return SURGERY_CITATIONS;
  return GENERAL_CITATIONS;
}

interface CitationsFooterProps {
  citations?: Citation[];
  department?: string;
  className?: string;
}

export function CitationsFooter({ citations, department, className = "" }: CitationsFooterProps) {
  const { lang } = useLanguage();
  const isAr = lang === "ar";

  // Use provided citations or get from department
  const refs = citations || (department ? getCitationsForDepartment(department) : GENERAL_CITATIONS);

  return (
    <section className={`mt-12 pt-8 border-t border-[#D4A843]/20 ${className}`} aria-label="References">
      {/* Medical Disclaimer */}
      <div className="flex items-start gap-3 mb-6 p-4 bg-[#D4A843]/5 rounded-lg border border-[#D4A843]/10">
        <Shield className="w-5 h-5 text-[#D4A843] mt-0.5 shrink-0" />
        <p className="text-sm text-[#5C5549]" dir={isAr ? "rtl" : "ltr"}>
          {isAr
            ? "هذه المعلومات لأغراض تعليمية عامة ولا تغني عن التقييم أو الاستشارة الطبية المتخصصة. لا يُعرض اسم مراجع طبي إلا بعد توثيق مراجعة الصفحة واعتمادها."
            : "This information is for general education and does not replace a clinical assessment or professional medical consultation. A named medical reviewer is shown only after page-specific review and approval are recorded."}
        </p>
      </div>

      {/* References Header */}
      <div className="flex items-center gap-2 mb-4">
        <BookOpen className="w-4 h-4 text-[#D4A843]" />
        <p className="text-base font-semibold text-[#2C2825]" dir={isAr ? "rtl" : "ltr"}>
          {isAr ? "المراجع والمصادر العلمية" : "Scientific References & Sources"}
        </p>
      </div>

      {/* Citations List */}
      <ol className="space-y-2 list-decimal list-inside" dir={isAr ? "rtl" : "ltr"}>
        {refs.map((ref) => (
          <li key={ref.id} className="text-xs text-[#5C5549] leading-relaxed">
            <a
              href={ref.url}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#D4A843] transition-colors inline-flex items-center gap-1"
            >
              <span className="font-medium">{ref.title}</span>
              <span className="text-[#8B8680]">— {ref.source}</span>
              {ref.year && <span className="text-[#8B8680]">({ref.year})</span>}
              <ExternalLink className="w-3 h-3 text-[#8B8680] shrink-0" />
            </a>
          </li>
        ))}
      </ol>

      {/* Authority Badge */}
      <div className="mt-6 flex items-center gap-2 text-xs text-[#8B8680]" dir={isAr ? "rtl" : "ltr"}>
        <Shield className="w-3.5 h-3.5 text-[#D4A843]" />
        <span>
          {isAr
            ? "مركز بيلا روز الطبي — دبي، الإمارات العربية المتحدة — لا يظهر اسم المراجع الطبي إلا بعد اعتماد خاص بالصفحة"
            : "Bella Rose Medical Center — Dubai, UAE — a medical reviewer is named only after page-specific approval"}
        </span>
      </div>
    </section>
  );
}
